import { useNavigate } from "@tanstack/react-router";

import type { ComponentProps } from "react";

import { SearchBar } from "@/features/directory/modules/search-bar/search-bar";
import { SearchResults } from "@/features/directory/modules/search-results/search-results";
import { searchTitle } from "@/features/directory/utils/search-head";
import type { DirectorySearch, DirectorySort } from "@/features/directory/utils/search-params";
import { PortalHead, PortalPage } from "@/features/portal/components/portal-chrome";

type ResultsProps = ComponentProps<typeof SearchResults>;

/**
 * Search the directory without signing in, the way Booksy and ZnanyLekarz work: what and where on
 * top, people with prices and open times below, and booking one tap from any time. The same page
 * answers `/search` and the directory's own addresses; changing anything goes through `/search`,
 * which sends a plain service or city back to its address.
 */
export function SearchPage({
  search,
  result,
  firstTimes,
  cities,
}: {
  search: DirectorySearch & { sort?: DirectorySort };
  result: ResultsProps["first"];
  firstTimes: ResultsProps["firstTimes"];
  cities: ResultsProps["cities"];
}) {
  const navigate = useNavigate();

  return (
    <PortalPage
      head={
        <PortalHead className="pb-10 md:pb-12">
          <div className="mx-auto flex max-w-[1080px] flex-col gap-6 pt-10 md:gap-8 md:pt-16">
            <h1 className="text-[clamp(2rem,4.4vw,3.5rem)] leading-[1.02] font-medium tracking-[-0.045em] text-balance">
              {searchTitle(search)}
            </h1>
            <SearchBar key={JSON.stringify(search)} initial={search} />
          </div>
        </PortalHead>
      }
    >
      <div className="mx-auto max-w-[1080px]">
        <SearchResults
          key={JSON.stringify({ ...search, total: result?.total })}
          search={search}
          first={result}
          firstTimes={firstTimes}
          cities={cities}
          onChange={(next) => void navigate({ to: "/search", search: next })}
        />
      </div>
    </PortalPage>
  );
}
